import { routes } from "../../assets/constants";
import { ApiMethod } from "../models/api-types.type";
import { useAuthContext } from "./use-auth-context.hook";

/** A YYYY-MM-DD range; both ends inclusive. */
export interface InsightsRange {
    startDate: string;
    endDate: string;
}

export interface ShopperOverview {
    range: InsightsRange;
    partial: boolean;
    totals: {
        conversations: number;
        shoppers: number;
        questions: number;
        replies: number;
        unanswered: number;
        answeredRate: number | null;
        feedbackUp: number;
        feedbackDown: number;
        roomPhotos: number;
        renders: number;
        productsShown: number;
        costUsd: number;
        avgLatencyMs: number | null;
        chatOpens: number;
        cartClicks: number;
        productClicks: number;
        seeInRoomClicks: number;
        promptImpressions: number;
        promptClicks: number;
        promptClickRate: number | null;
    };
    sources: Record<string, number>;
    entries: Record<string, number>;
    devices: Record<string, number>;
    /** Per reply: answered, no_results, tool_error, error, no_reply. */
    outcomes: Record<string, number>;
    /** Per conversation, one each — absent from an API older than the inbox's outcome filter. */
    conversationOutcomes?: Record<ConversationOutcome, number>;
    actions: Record<string, number>;
    series: Array<{ date: string; conversations: number; questions: number; cartClicks: number }>;
}

export interface ShopperQuestionEntry {
    threadUuid: string;
    at: string;
    question: string;
    answer: string;
    sku: string | null;
    outcome: string | null;
}

export interface ShopperQuestions {
    partial: boolean;
    topQuestions: Array<{ question: string; count: number }>;
    topProducts: Array<{ sku: string; count: number }>;
    topRecommended: Array<{ sku: string; name: string | null; count: number }>;
    searchFilters: Record<string, Array<{ value: string; count: number }>>;
    emptySearches: Array<{ filters: string; count: number }>;
    unanswered: ShopperQuestionEntry[];
    disliked: ShopperQuestionEntry[];
    /** What the questions are about, largest first. Absent from an older API. */
    themes?: ShopperQuestionTheme[];
}

/** delivery, price, stock, size, colour, material, suitability, … — see shopper-themes.ts in the API. */
export type QuestionThemeKey =
    | 'delivery' | 'returns' | 'samples' | 'store' | 'quantity' | 'price' | 'installation' | 'care'
    | 'size' | 'colour' | 'material' | 'suitability' | 'stock' | 'products' | 'other';

export interface ShopperQuestionTheme {
    theme: QuestionThemeKey | string;
    count: number;
    unanswered: number;
    examples: Array<{ question: string; count: number; unanswered: number }>;
}

/**
 * How a conversation ended, one per conversation, in priority order: a reply
 * it could not give, the product shown in the shopper's room, a product
 * recommended, or simply answered.
 */
export type ConversationOutcome = 'unanswered' | 'room' | 'recommended' | 'answered';

export interface ShownProduct {
    uuid: string | null;
    sku: string | null;
    name: string | null;
    image: string | null;
}

export interface ConversationSummary {
    uuid: string;
    deviceToken: string;
    startedAt: string;
    lastMessageAt: string;
    productSku: string | null;
    firstQuestion: string;
    questions: number;
    unanswered: number;
    feedbackUp: number;
    feedbackDown: number;
    roomPhotos: number;
    costUsd: number;
    entry: string | null;
    deviceType: string | null;
    pageUrl: string | null;
    utm: Record<string, string> | null;
    /** Absent from an older API; derive it with `conversationOutcomeOf`. */
    outcome?: ConversationOutcome;
    /** Up to four, for the row's thumbnails. */
    products?: ShownProduct[];
    productsShown?: number;
    /** The shop's team in it, when a shopper asked for a person. Absent from an older API. */
    handoff?: TeamHandoff | null;
    teamReplies?: number;
}

/** Where the shop's team stands in a conversation (the API's shopper-handoff `teamHandoff`). */
export interface TeamHandoff {
    status: 'requested' | 'active' | 'closed';
    reason: 'shopper_asked' | 'ai_unanswered' | 'order_support';
    requestedAt: string | null;
    joinedAt: string | null;
    closedAt: string | null;
    agentName: string | null;
    agentId: string | null;
    emailSaved: boolean;
    /** What the shopper typed into "send me the reply by email", with their consent. */
    shopperEmail: string | null;
    shopperName: string | null;
    unreadForTeam: number;
}

export interface HandoffSummary {
    waiting: number;
    active: number;
    enabled: boolean;
}

/** The inbox's team filters: waiting for a person, or with the team now. */
export type HandoffFilter = 'waiting' | 'live';

export interface ConversationDetail extends ConversationSummary {
    context: Record<string, any> | null;
    messages: Array<Record<string, any>>;
    events: Array<{ type: string; at: string; messageId?: string; productSku?: string; data?: Record<string, any> }>;
}

export interface ConversationsPage {
    data: ConversationSummary[];
    pagination: { page: number; limit: number; total: number; totalPages: number; hasNext: boolean; hasPrev: boolean };
    /** Conversations in the period the plan does not list (Free lists the newest 10). Absent on older APIs. */
    lockedCount?: number;
}

/** What the nudge did with a group of devices — nudged, or held out of it. */
export interface ReturningGroupOutcome {
    devices: number;
    openedChat: number;
    sawInRoom: number;
    clickedCart: number;
    cartRate: number | null;
}

/** GET shopper-insights/returning: the returning-visitor nudge over a period. */
export interface ReturningReport {
    nudges: { shown: number; clicked: number; heldOut: number; clickRate: number | null };
    nudged: ReturningGroupOutcome;
    holdout: ReturningGroupOutcome;
    topProducts: Array<{ sku: string; returningDevices: number }>;
    partial: boolean;
}

/** Which consent source the SDK last found on the vendor's site. `none` = no banner it could read. */
export interface ConsentSeen {
    source: 'set_consent' | 'not_required' | 'tcf' | 'cookiebot' | 'onetrust' | 'didomi' | 'axeptio' | 'shopify'
        | 'google_consent_mode' | 'ardealy_banner' | 'none' | null;
    at: string | null;
    granted: boolean | null;
}

const query = (params: Record<string, string | number | boolean | undefined>) => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== '') search.set(key, String(value));
    });
    const text = search.toString();
    return text ? `?${text}` : '';
};

/** The Shopping Assistant pages: what the vendor's shopper conversations add up to. */
export const useShopperInsightsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const getOverview = async (range: InsightsRange) =>
        await sendAuthGuardedRequest(ApiMethod.GET, routes.shopperInsights.overview + query({ ...range })) as ShopperOverview;

    const getQuestions = async (range: InsightsRange) =>
        await sendAuthGuardedRequest(ApiMethod.GET, routes.shopperInsights.questions + query({ ...range })) as ShopperQuestions;

    const getConversations = async (
        params: InsightsRange & { page?: number; limit?: number; search?: string; unansweredOnly?: boolean; outcome?: ConversationOutcome; handoff?: HandoffFilter },
    ) => await sendAuthGuardedRequest(
        ApiMethod.GET,
        routes.shopperInsights.conversations + query({
            ...params,
            // An API older than `outcome` still honours this spelling of the one filter it had.
            unansweredOnly: params.unansweredOnly || params.outcome === 'unanswered' || undefined,
        }),
    ) as ConversationsPage;

    const getConversation = async (uuid: string) =>
        await sendAuthGuardedRequest(ApiMethod.GET, routes.shopperInsights.conversation(uuid)) as ConversationDetail;

    const getReturning = async (range: InsightsRange) =>
        await sendAuthGuardedRequest(ApiMethod.GET, routes.shopperInsights.returning + query({ ...range })) as ReturningReport;

    const getConsentSeen = async () =>
        await sendAuthGuardedRequest(ApiMethod.GET, routes.shopperInsights.consentSeen) as ConsentSeen;

    const getHandoffSummary = async () =>
        await sendAuthGuardedRequest(ApiMethod.GET, routes.shopperInsights.handoffSummary) as HandoffSummary;

    /** Answer the shopper as the team; the assistant stays quiet until the conversation is handed back. */
    const reply = async (uuid: string, text: string) =>
        await sendAuthGuardedRequest(ApiMethod.POST, routes.shopperInsights.reply(uuid), { text }) as {
            handoff: TeamHandoff;
            message: { id: string; role: 'team'; text: string; at: string; author: { name: string | null } };
        };

    const take = async (uuid: string) =>
        await sendAuthGuardedRequest(ApiMethod.POST, routes.shopperInsights.take(uuid), {}) as { handoff: TeamHandoff };

    /** Hand the conversation back to the assistant. */
    const close = async (uuid: string) =>
        await sendAuthGuardedRequest(ApiMethod.POST, routes.shopperInsights.close(uuid), {}) as { handoff: TeamHandoff | null };

    return { getOverview, getQuestions, getConversations, getConversation, getReturning, getConsentSeen, getHandoffSummary, reply, take, close };
};
